<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, onBeforeUnmount, ref, useAttrs, watch } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    position?: 'center' | 'bottom' | 'top'
    closable?: boolean
    closeOnBackdrop?: boolean
    width?: string | number
    background?: string
    title?: string
    closeDirection?: string
  }>(),
  {
    position: 'center',
    closable: true,
    closeOnBackdrop: true,
    background: '#f5f7fa',
  }
)

const emit = defineEmits(['update:modelValue'])
const modalContentRef = ref<HTMLElement>()

const attrs = useAttrs()

const close = () => emit('update:modelValue', false)

onClickOutside(modalContentRef, () => {
  if (props.modelValue && props.closeOnBackdrop) {
    close()
  }
})

const modalPositionClass = computed(() => {
  switch (props.position) {
    case 'bottom':
      return 'items-end'
    case 'top':
      return 'items-start'
    default:
      return 'items-center'
  }
})

const modalStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) style.width = typeof props.width === 'number' ? props.width + 'px' : props.width
  style.background = props.background
  return style
})

// watch(
//   () => props.modelValue,
//   (newValue) => {
//     document.body.style.overflow = newValue ? 'hidden' : ''
//   }
// )
watch(
  () => props.modelValue,
  (newValue) => {
    document.body.style.overflow = newValue ? 'hidden' : ''
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <teleport to="body">
    <transition name="modal-scale">
      <div v-if="modelValue" v-bind="attrs" class="fixed inset-0 z-9999 flex justify-center"
        :class="modalPositionClass">

        <!-- backdrop -->
        <div class="absolute inset-0 bg-[rgba(46,67,105,.502)] backdrop-blur-xs cursor-pointer"
          :class="props.closeOnBackdrop === false ? 'pointer-events-none' : ''"
          @click="props.closeOnBackdrop && close()"></div>

        <div ref="modalContentRef"
          class="main-content-modal relative z-10000 rounded-2xl shadow-lg px-6 max-h-[85vh] overflow-y-auto" :class="[
            position === 'bottom' ? 'rounded-b-none w-full' : '',
            position === 'top' ? 'rounded-t-none w-full' : 'w-[90%] m-4',
          ]" :style="modalStyle">
          <div v-if="title || closable"
            class="sticky top-0 bg-white z-60 flex justify-between items-center px-4 pt-6 pb-2"
            :class="closeDirection === 'left' ? 'flex-row' : 'flex-row-reverse'">
            <span v-if="title" class="text-lg font-medium text-[#1b325a]">{{ title }}</span>
            <button v-if="props.closable" @click="close"
              class="cursor-pointer text-gray-600 hover:text-black">✕</button>
          </div>

          <slot :close="close" />
        </div>
      </div>
    </transition>
  </teleport>
</template>
